/**
 * Converts TypedValue JSON (from GraphQL) into React nodes using the shared
 * TagValueTypedValueViewer — the same leaf renderer used by structured tables.
 */
import {createStyles, Stack, Text} from '@mantine/core';
import React from 'react';

import TagValueTypedValueViewer from 'notebook/tables/typed-values/components/viewers/TagValueTypedValueViewer';
import {formatTypedValue} from 'notebook/tables/typed-values/DataTypes';
import type {TypedValue} from 'notebook/tables/typed-values/TypedValueConstants';
import type {ProcedureParameterDataTypeDefinition} from 'procedures/ProcedureDomainTypes';
import type {UnitSummary} from 'procedures/shared-components/schema-field-type-selectors/procedureSchemaFieldConstants';
import {SchemaFieldRenderData} from 'schemas/fields/SchemaFieldConstants';
import {isItemLinkFieldType} from 'tag-schema/util/TagSchemaUtil';
import getTestProps from 'test/lib/cypress/getTestProps';
import {ChipSize} from 'ui-library/chips/Chip';
import ProcedureMethodExecutionInstanceChip from 'ui-library/chips/ProcedureMethodExecutionInstanceChip';

import MultiValueDisplay from './MultiValueDisplay';
import type {MaterialInputValue} from './PMEICellColorGrade';
import type {ColumnKind} from './PMEICellTooltipContent';
import {formatMaterialInputAmount} from './PMEIMaterialInputFormat';
import type {LinkedPMEI} from './PMEIMaterialLinkUtils';
import {
  compareValueToRange,
  isComparableTypedValue,
  isOutOfRange,
  type MinMaxTypedValue,
} from './PMEIParameterRange';

const useStyles = createStyles((theme) => ({
  unrecordedLink: {
    color: theme.colors.blue[7],
    textDecoration: 'none',
    '&:hover, &:focus-visible': {
      textDecoration: 'underline',
    },
  },
}));

const UNRECORDED_LABEL: Record<ColumnKind, string> = {
  confirmed: 'Not confirmed',
  measured: 'Not measured',
};

/**
 * Stands in for a Confirmed or Measured cell whose parameter has nothing recorded at all, so the
 * column reads as deliberately unfilled rather than as a rendering gap. It links out to the PMEI's
 * worksheet — the same destination as the panel's "Open Worksheet" button — because that is where
 * the value gets recorded, and degrades to plain text when no worksheet is accessible.
 *
 * A recorded-but-blank value is *not* unrecorded: those cells keep their (possibly empty) rendered
 * content, since something was entered on the worksheet even if it shows as nothing here.
 */
export function UnrecordedValue({
  columnKind,
  worksheetUrl,
}: {
  columnKind: ColumnKind;
  worksheetUrl: string | null;
}): JSX.Element {
  const {classes} = useStyles();
  const label = UNRECORDED_LABEL[columnKind];
  const testProps = getTestProps({
    component: 'PMEIValueRenderer',
    element: `unrecorded-${columnKind}`,
  });

  if (worksheetUrl == null) {
    return (
      <Text fz="sm" c="dimmed" {...testProps}>
        {label}
      </Text>
    );
  }

  return (
    <Text
      component="a"
      href={worksheetUrl}
      target="_blank"
      rel="noopener noreferrer"
      fz="sm"
      className={classes.unrecordedLink}
      {...testProps}
    >
      {label}
    </Text>
  );
}

function buildSchemaFieldRenderData(dataType: ProcedureParameterDataTypeDefinition): SchemaFieldRenderData {
  const unitSymbol = dataType.unit != null ? dataType.unit.symbol : null;
  return {name: '', fieldType: dataType.type, unitSymbol};
}

function isEntityLinkField(dataType: ProcedureParameterDataTypeDefinition): boolean {
  return isItemLinkFieldType(dataType.type);
}

function renderTypedValue(
  typedValue: TypedValue,
  textValue: string,
  schemaFieldRenderData: SchemaFieldRenderData,
  showDateChips: boolean
): React.ReactNode {
  return (
    <TagValueTypedValueViewer
      chipSize={ChipSize.TABLE_CELL}
      shouldPreviewImages={false}
      showChipsAsLinks={true}
      summarize={false}
      summarizeThreshold={10}
      typedValue={typedValue}
      textValue={textValue}
      schemaFieldRenderData={schemaFieldRenderData}
      shouldShowDateAndDatetimesAsChips={showDateChips}
    />
  );
}

function renderEntityValues(
  values: Array<TypedValue>,
  schemaFieldRenderData: SchemaFieldRenderData,
  disableReadingsHoverCard: boolean
): React.ReactNode | null {
  if (values.length === 0) {
    return null;
  }
  const rendered = values.map((v) => renderTypedValue(v, '', schemaFieldRenderData, false));
  return <MultiValueDisplay values={rendered} disableReadingsHoverCard={disableReadingsHoverCard} />;
}

function renderParameterValue(
  typedValue: TypedValue,
  schemaFieldRenderData: SchemaFieldRenderData,
  minimum: MinMaxTypedValue,
  maximum: MinMaxTypedValue
): React.ReactNode | null {
  if (typedValue == null) {
    return null;
  }

  const textValue = formatTypedValue(typedValue);

  if (!textValue) {
    return null;
  }

  const rendered = renderTypedValue(typedValue, textValue, schemaFieldRenderData, true);
  if (isComparableTypedValue(typedValue) && isOutOfRange(compareValueToRange(typedValue, minimum, maximum))) {
    return (
      <Text
        span={true}
        inherit={true}
        fw="bold"
        {...getTestProps({component: 'PMEIValueRenderer', element: 'out-of-range-value'})}
      >
        {rendered}
      </Text>
    );
  }
  return rendered;
}

function getMaterialInputParts(
  material: TypedValue,
  amount: TypedValue | null | undefined,
  schemaFieldRenderData: SchemaFieldRenderData,
  amountUnit: UnitSummary | null
): {materialNode: React.ReactNode | null; amountText: string | null} | null {
  const materialText = formatTypedValue(material);
  const amountText = formatMaterialInputAmount(amount, amountUnit);
  // Only bail when there is nothing to show at all. An amount recorded without a material (e.g. an
  // unplanned confirmed amount) must still render, so materialNode is left null in that case rather
  // than dropping the whole value.
  if (!materialText && amountText == null) {
    return null;
  }
  const materialNode = materialText
    ? renderTypedValue(material, materialText, schemaFieldRenderData, false)
    : null;
  return {materialNode, amountText};
}

/** Cell display: material stacked above its amount, left-aligned. */
function renderMaterialInputValueStacked(
  material: TypedValue,
  amount: TypedValue | null | undefined,
  schemaFieldRenderData: SchemaFieldRenderData,
  amountUnit: UnitSummary | null
): React.ReactNode | null {
  const parts = getMaterialInputParts(material, amount, schemaFieldRenderData, amountUnit);
  if (parts == null) {
    return null;
  }
  if (parts.amountText == null) {
    return parts.materialNode;
  }
  // Amount recorded without a material: render the amount exactly as it appears beneath a material,
  // just without the material line above it.
  if (parts.materialNode == null) {
    return (
      <Text fz="xs" c="dimmed">
        ({parts.amountText})
      </Text>
    );
  }
  return (
    <Stack spacing={2} align="flex-start">
      {parts.materialNode}
      <Text fz="xs" c="dimmed">
        ({parts.amountText})
      </Text>
    </Stack>
  );
}

/** Tooltip display: material and amount inline as "material (amount)" so a long values list stays compact. */
function renderMaterialInputValueInline(
  material: TypedValue,
  amount: TypedValue | null | undefined,
  schemaFieldRenderData: SchemaFieldRenderData,
  amountUnit: UnitSummary | null
): React.ReactNode | null {
  const parts = getMaterialInputParts(material, amount, schemaFieldRenderData, amountUnit);
  if (parts == null) {
    return null;
  }
  if (parts.amountText == null) {
    return parts.materialNode;
  }
  // Amount recorded without a material: keep the parentheses inline so a bare number does not read
  // ambiguously within a comma/arrow-joined values list.
  if (parts.materialNode == null) {
    return (
      <Text span={true} inherit={true} c="dimmed">
        ({parts.amountText})
      </Text>
    );
  }
  return (
    <>
      {parts.materialNode}{' '}
      <Text span={true} inherit={true} c="dimmed">
        ({parts.amountText})
      </Text>
    </>
  );
}

export function buildMaterialInputCell(
  values: Array<MaterialInputValue>,
  dataType: ProcedureParameterDataTypeDefinition,
  amountUnit: UnitSummary | null,
  // When the cell also carries a differences tooltip, the tooltip owns the hover and the values
  // "(N values)" cue must render as plain non-link text so the two overlays never collide.
  disableReadingsHoverCard = false
): React.ReactNode {
  const schema = buildSchemaFieldRenderData(dataType);
  const rendered = values
    .map(({material, amount}) => renderMaterialInputValueStacked(material, amount, schema, amountUnit))
    .filter((v): v is React.ReactNode => v != null);
  const tooltipRendered = values
    .map(({material, amount}) => renderMaterialInputValueInline(material, amount, schema, amountUnit))
    .filter((v): v is React.ReactNode => v != null);
  return (
    <MultiValueDisplay
      values={rendered}
      tooltipValues={tooltipRendered}
      disableReadingsHoverCard={disableReadingsHoverCard}
    />
  );
}

export function buildCell({
  values,
  dataType,
  minimum = null,
  maximum = null,
  // When the cell also carries a differences tooltip, the tooltip owns the hover and the values
  // "(N values)" cue must render as plain non-link text so the two overlays never collide.
  disableReadingsHoverCard = false,
}: {
  values: Array<TypedValue>;
  dataType: ProcedureParameterDataTypeDefinition;
  minimum?: MinMaxTypedValue;
  maximum?: MinMaxTypedValue;
  disableReadingsHoverCard?: boolean;
}): React.ReactNode {
  const filteredValues = values.filter((v) => v != null);
  if (filteredValues.length === 0) {
    return null;
  }
  const schema = buildSchemaFieldRenderData(dataType);
  if (isEntityLinkField(dataType)) {
    return renderEntityValues(filteredValues, schema, disableReadingsHoverCard);
  }
  const rendered = filteredValues
    .map((v) => renderParameterValue(v, schema, minimum, maximum))
    // This filter for null is needed because renderParameterValue can return null.
    .filter((v): v is React.ReactNode => v != null);
  return <MultiValueDisplay values={rendered} disableReadingsHoverCard={disableReadingsHoverCard} />;
}

function renderLinkCell(
  pmeis: Array<LinkedPMEI>,
  onNavigateToPmei: ((pmeiId: string) => void) | null
): React.ReactNode {
  if (pmeis.length === 0) {
    return (
      <Text fz="sm" c="dimmed" fs="italic">
        Pending
      </Text>
    );
  }
  const chips = pmeis.map((pmei) => (
    <ProcedureMethodExecutionInstanceChip
      key={pmei.id}
      id={pmei.id}
      size={ChipSize.SMALL}
      displayNameOverride={`${pmei.methodName} · ${pmei.conditionName}-${pmei.replicateNumber}`}
      {...(onNavigateToPmei != null
        ? {
            onClick: () => {
              onNavigateToPmei(pmei.id);
            },
          }
        : {})}
    />
  ));
  return <MultiValueDisplay values={chips} />;
}

export function renderMaterialLinkCell(
  versionId: string,
  index: Map<string, Array<LinkedPMEI>> | null,
  noEdgeLabel: string,
  onNavigateToPmei: ((pmeiId: string) => void) | null
): React.ReactNode {
  if (index == null) {
    return null;
  }
  const linkedPMEIs = index.get(versionId);
  if (linkedPMEIs == null) {
    return (
      <Text fz="sm" c="dimmed">
        {noEdgeLabel}
      </Text>
    );
  }
  return renderLinkCell(linkedPMEIs, onNavigateToPmei);
}
